/**
 * Helper functions for ArcGIS Map centerline detection
 * Works with GeoJSON parsed from KML via @tmcw/togeojson
 */
import * as toGeoJSON from '@tmcw/togeojson';
import length from '@turf/length';
import lineSliceAlong from '@turf/line-slice-along';

// KML and GeoJSON helpers
/**
 * Fetch and parse KML to GeoJSON
 * @param {string} kmlUrl - URL to the KML file
 * @returns {Promise<Object>} - GeoJSON FeatureCollection
 */
const parseKMLToGeoJSON = async kmlUrl => {
  const response = await fetch(kmlUrl);
  const kmlText = await response.text();
  const xmlDoc = new DOMParser().parseFromString(kmlText, 'text/xml');

  // Check for parsing errors
  const parserError = xmlDoc.querySelector('parsererror');
  if (parserError) {
    throw new Error(`KML parsing error: ${parserError.textContent}`);
  }
  return toGeoJSON.kml(xmlDoc);
};

/**
 * Find all LineString features from a GeoJSON FeatureCollection
 * @param {Object} geojson - GeoJSON FeatureCollection
 * @returns {Array} - array of LineString features
 */
const findLineStringFeatures = geojson => {
  if (!geojson || !geojson.features) {
    return [];
  }
  return geojson.features.filter(feature => {
    var _feature$geometry;
    const type = feature === null || feature === void 0 || (_feature$geometry = feature.geometry) === null || _feature$geometry === void 0 ? void 0 : _feature$geometry.type;
    return type === 'LineString' || type === 'MultiLineString';
  });
};

/**
 * Select the longest LineString from an array of features
 * @param {Array} features - array of GeoJSON LineString features
 * @returns {Object|null} - the longest feature, or null if none found
 */
const selectLongestLineString = features => {
  if (!features || features.length === 0) {
    return null;
  }
  if (features.length === 1) {
    return features[0];
  }
  let longestFeature = null;
  let maxLength = 0;
  features.forEach(feature => {
    // Use Turf's length function (returns kilometers by default)
    const featureLength = length(feature);
    if (featureLength > maxLength) {
      maxLength = featureLength;
      longestFeature = feature;
    }
  });
  return longestFeature;
};

// Geometry helpers
/**
 * Extract the start and end points from a GeoJSON LineString feature
 * @param {Object} feature - GeoJSON feature with LineString or MultiLineString geometry
 * @returns {Object} - { startPoint, endPoint } or null if invalid
 */
const extractEndpoints = feature => {
  if (!feature || !feature.geometry) {
    return null;
  }
  const {
    geometry
  } = feature;
  const {
    type,
    coordinates
  } = geometry;
  if (type === 'MultiLineString') {
    // Get first point of first line, last point of last line
    const firstLine = coordinates[0];
    const lastLine = coordinates[coordinates.length - 1];
    if (!(firstLine !== null && firstLine !== void 0 && firstLine.length) || !(lastLine !== null && lastLine !== void 0 && lastLine.length)) {
      return null;
    }
    return {
      startPoint: {
        x: firstLine[0][0],
        y: firstLine[0][1]
      },
      endPoint: {
        x: lastLine[lastLine.length - 1][0],
        y: lastLine[lastLine.length - 1][1]
      }
    };
  }

  // LineString
  if (!coordinates || coordinates.length < 2) {
    return null;
  }
  const startCoords = coordinates[0];
  const endCoords = coordinates[coordinates.length - 1];
  return {
    startPoint: {
      x: startCoords[0],
      y: startCoords[1]
    },
    endPoint: {
      x: endCoords[0],
      y: endCoords[1]
    }
  };
};

/**
 * Convert GeoJSON coordinates to ArcGIS polyline paths format
 * @param {Object} feature - GeoJSON LineString or MultiLineString feature
 * @returns {Array} - array of paths for ArcGIS Polyline
 */
const geojsonToArcGISPaths = feature => {
  if (!feature || !feature.geometry) {
    return [];
  }
  const {
    geometry
  } = feature;
  if (geometry.type === 'LineString') {
    return [geometry.coordinates];
  }
  if (geometry.type === 'MultiLineString') {
    return geometry.coordinates;
  }
  return [];
};

/**
 * Get the total length of a GeoJSON line feature
 * @param {Object} line - GeoJSON LineString or MultiLineString feature
 * @returns {number} - Length in feet, or 0 if invalid
 */
const getLineLength = line => {
  if (!(line !== null && line !== void 0 && line.geometry)) return 0;
  return length(line, {
    units: 'feet'
  });
};

/**
 * Get a line segment between two distances along a line
 * Useful for drawing activity segments by station range
 * @param {Object} line - GeoJSON LineString feature
 * @param {number} startDistance - Start distance in feet
 * @param {number} endDistance - End distance in feet
 * @returns {Object|null} - GeoJSON LineString feature, or null if invalid
 */
const getLineSlice = (line, startDistance, endDistance) => {
  if (!(line !== null && line !== void 0 && line.geometry)) return null;
  const totalLength = length(line, {
    units: 'feet'
  });
  const clampedStart = Math.max(0, Math.min(startDistance, totalLength));
  const clampedEnd = Math.max(clampedStart, Math.min(endDistance, totalLength));

  // Avoid zero-length slices which cause turf to throw
  if (clampedStart >= clampedEnd) {
    return null;
  }
  try {
    return lineSliceAlong(line, clampedStart, clampedEnd, {
      units: 'feet'
    });
  } catch (_unused) {
    // Handle edge cases where turf can't compute a valid slice
    return null;
  }
};

// Segment helpers
const buildSegmentAttributes = (activity, cabinetIdentifier, defaultActivityType) => {
  const {
    fromDistance,
    toDistance,
    activityType,
    formID,
    formTypeIdentifier,
    cabinetIdentifier: activityCabinetIdentifier,
    displayFields
  } = activity;
  return {
    activityType: activityType !== null && activityType !== void 0 ? activityType : defaultActivityType,
    fromDistance,
    toDistance,
    formID: formID || null,
    formTypeIdentifier: formTypeIdentifier || null,
    cabinetIdentifier: activityCabinetIdentifier || cabinetIdentifier || null,
    displayFields: Array.isArray(displayFields) ? displayFields : null
  };
};
export { parseKMLToGeoJSON, findLineStringFeatures, selectLongestLineString, extractEndpoints, geojsonToArcGISPaths, getLineLength, getLineSlice, buildSegmentAttributes };